import IconReset from "assets/img/icon-reset.svg?react"

import { FC, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import PQueue from "p-queue"
import dayjs from "utils/dayjs"

import { Guid, ICreateImageFile, IValidationRule } from "@/types"
import AssetInspection, { AssetInspectionType } from "models/AssetInspection"
import ConfirmModal from "components/ConfirmModal/ConfirmModal"
import Form from "components/Form/Form"
import useInputNumber from "components/InputNumber/hooks/useInputNumber"
import CustomError from "models/CustomError"
import ValidationService from "services/ValidationService"
import toastMessages from "stores/toastMessages"
import { INPUT_MAX_LENGTH_LARGE } from "config/constants"
import AssetService from "services/AssetService"
import FormField from "components/FormField/FormField"
import { Grid, GridItem } from "components/Grid"
import Radio from "components/Radio/Radio"
import ValidationMessage from "components/ValidationMessage/ValidationMessage"
import Input from "components/Input/Input"
import InputNumber from "components/InputNumber/InputNumber"
import SliderCondition from "components/Slider/SliderCondition"
import SliderFuel from "components/Slider/SliderFuel"
import AssetInspectionModalImageEditor from "./AssetInspectionModalImageEditor"
import { IFileToAdd } from "components/AddPicturesModal/AddPicturesModal"
import FileService from "services/FileService"
import { Flex, FlexItem } from "components/Flex"
import { useTenantSettings } from "stores/tenantSettings"
import AssetInspectionModalImageViewer from "./AssetInspectionModalImageViewer"
import { formatNumber, toDateTimeUtc } from "utils/utils"
import NoValue from "components/NoValue/NoValue"
import Button from "components/Button/Button"

interface IAssetInspectionModalProps {
    assetId: Guid
    shippingOrderId: Guid
    inspectionType: AssetInspectionType
    latestCondition?: number
    latestFuel?: number
    latestOperationHours?: number
    latestMileage?: number
    latestAdBlue?: number
    latestRefuel?: number
    latestLsva?: number
    inspection?: AssetInspection | null
    isMachine?: boolean
    onClose: () => void
    onInspectionCreatedOrUpdated?: () => void
}

const AssetInspectionModal: FC<IAssetInspectionModalProps> = ({
    assetId,
    shippingOrderId,
    inspectionType,
    latestCondition,
    latestFuel,
    latestOperationHours,
    latestMileage,
    latestAdBlue,
    latestRefuel,
    latestLsva,
    inspection,
    isMachine,
    onClose,
    onInspectionCreatedOrUpdated,
}) => {
    const { t } = useTranslation()
    const { tenantSettingsLanguages } = useTenantSettings()
    const tenantPrimaryLanguage = tenantSettingsLanguages.primaryLanguage.isoCode
    const isEdit = !!inspection
    const canEditAllFields = !inspection || !!inspection?.linkUpdateInspection
    const isFuelRequired = isMachine
    const isOperationHoursRequired = isMachine

    const [damages, setDamages] = useState<boolean | null>(inspection?.damages ?? null)
    const [isOperational, setIsOperational] = useState<boolean | null>(inspection?.isOperational ?? null)
    const [condition, setCondition] = useState<number | null>(inspection?.condition ?? null)
    const [fuel, setFuel] = useState<number | null>(inspection?.fuel ?? null)
    const [operationHours, operationHoursNumber, setOperationHours] = useInputNumber(inspection?.operationHours ?? latestOperationHours ?? "")
    const [mileage, mileageNumber, setMileage] = useInputNumber(inspection?.mileage ?? latestMileage ?? "")
    const [adBlue, setAdBlue] = useState<number | null>(inspection?.adBlue ?? null)
    const [refuel, refuelNumber, setRefuel] = useInputNumber(inspection?.refuel ?? latestRefuel ?? "")
    const [lsva, lsvaNumber, setLsva] = useInputNumber(inspection?.lsva ?? latestLsva ?? "")
    const [comment, setComment] = useState<string | null>(inspection?.comment ?? null)
    const [isSubmitted, setIsSubmitted] = useState(false)
    const [isSubmitting, setIsSubmitting] = useState(false)

    const [imageCollectionFilesToUpload, setImageCollectionFilesToUpload] = useState<IFileToAdd[]>([])
    const [imageCollectionTags, setImageCollectionTags] = useState<string[]>([])
    const imageCollectionFixedTags = [
        t(`assetInspection.imageCollectionTag.inspection.${tenantPrimaryLanguage}`),
        inspectionType === AssetInspectionType.delivery
            ? t(`assetInspection.imageCollectionTag.delivery.${tenantPrimaryLanguage}`)
            : t(`assetInspection.imageCollectionTag.return.${tenantPrimaryLanguage}`)
    ]

    const validationErrors = useMemo((): { [x: string]: CustomError } => {
        if (!canEditAllFields) return {}

        const validationFields: [string, IValidationRule, any][] = [
            ["damages", { requiredRadio: true }, damages],
            ["isOperational", { requiredRadio: true }, isOperational],
            ["condition", { requiredSlider: true }, condition],
            ["fuel", { requiredSlider: isFuelRequired }, fuel],
            ["operationHours", { required: isOperationHoursRequired, "type": "integer", min: 0 }, operationHours],
            ["mileage", { "type": "integer", min: 0 }, mileage],
            ["refuel", { "type": "decimal", min: 0 }, refuel],
            ["lsva", { "type": "decimal", min: 0 }, lsva],
        ]

        if (!isEdit) {
            validationFields.push(["images", { required: true }, imageCollectionFilesToUpload.length > 0 ? true : undefined])
        }

        return ValidationService.validateMultiple(validationFields)
    }, [canEditAllFields, condition, damages, fuel, imageCollectionFilesToUpload.length, isEdit, isFuelRequired,
        isOperationHoursRequired, isOperational, lsva, mileage, operationHours, refuel])

    function handleImagesChange(files: IFileToAdd[], tags: string[]) {
        setImageCollectionFilesToUpload(files)
        setImageCollectionTags(tags)
    }


    async function createImageCollection() {
        const queue = new PQueue({ concurrency: 1 })
        const fileIds: string[] = []

        const uploadFile = async (file: File) => {
            const uploadedFile = await FileService.uploadFile<ICreateImageFile>(file)
            fileIds.push(uploadedFile.id)
        }

        for (const file of imageCollectionFilesToUpload) {
            queue.add(uploadFile.bind(null, file.file))
        }

        await queue.onIdle()

        if (fileIds.length === 0) {
            throw new Error()
        }

        return AssetService.createAssetImageCollection({
            assetId,
            fileIds,
            tags: [...imageCollectionFixedTags, ...imageCollectionTags],
        })

    }

    async function handleSubmit() {
        setIsSubmitted(true)
        if (Object.keys(validationErrors).length) {
            return
        }

        setIsSubmitting(true)

        let imageCollectionId: Guid | null = null

        try {
            if (isEdit) {
                if (inspection.linkUpdateInspection) {
                    await AssetService.updateAssetInspection(inspection.linkUpdateInspection, {
                        damages: damages!,
                        isOperational: isOperational!,
                        condition: condition!,
                        fuel,
                        operationHours: operationHoursNumber,
                        mileage: mileageNumber,
                        adBlue,
                        refuel: refuelNumber,
                        lsva: lsvaNumber,
                        comment,
                    })
                } else if (inspection.linkUpdateInspectionComment) {
                    await AssetService.updateAssetInspection(inspection.linkUpdateInspectionComment, {
                        comment
                    })
                } else {
                    throw new Error("Missing linkUpdateInspection")
                }
            } else {
                try {
                    imageCollectionId = await createImageCollection()
                } catch (e) {
                    console.error("Error uploading files or creating image collection", e)
                    toastMessages.showSystemError()
                    setIsSubmitting(false)
                    return
                }

                await AssetService.createAssetInspection({
                    assetId,
                    shippingOrderId,
                    damages: damages!,
                    isOperational: isOperational!,
                    condition: condition!,
                    fuel,
                    operationHours: operationHoursNumber,
                    mileage: mileageNumber,
                    adBlue,
                    refuel: refuelNumber,
                    lsva: lsvaNumber,
                    comment,
                    imageCollectionId,
                    inspectionDate: toDateTimeUtc(dayjs()),
                })
            }
        } catch (e) {
            console.error(e)
            toastMessages.showSystemError()
            setIsSubmitting(false)
            return
        }

        onClose()
        onInspectionCreatedOrUpdated?.()
    }

    return (
        <>
            <ConfirmModal
                title={inspectionType === AssetInspectionType.delivery
                    ? t(isEdit ? 'assetInspection.editDeliveryInspection' : 'assetInspection.deliveryInspection')
                    : t(isEdit ? 'assetInspection.editReturnInspection' : 'assetInspection.returnInspection')
                }
                maxWidth="586px"
                onClose={onClose}
                onConfirm={handleSubmit}
                confirmButtonLabel={t('common.save')}
                isBusy={isSubmitting}>
                <Form onSubmit={handleSubmit}>
                    <Grid columns="1fr" gap="2.4rem">
                        <FormField label={t('assetInspection.damages')}>
                            <Grid columns="minmax(0, 1fr) minmax(0, 1fr)" gap="2.4rem">
                                <Radio
                                    name="damages"
                                    label={t('common.yes')}
                                    checked={damages === true}
                                    onChange={() => setDamages(true)}
                                    disabled={!canEditAllFields}
                                />
                                <Radio
                                    name="damages"
                                    label={t('common.no')}
                                    checked={damages === false}
                                    onChange={() => setDamages(false)}
                                    disabled={!canEditAllFields}
                                />
                            </Grid>
                            {isSubmitted && validationErrors.damages &&
                                <ValidationMessage>{t(validationErrors.damages.message)}</ValidationMessage>
                            }
                        </FormField>
                        <FormField label={t('asset.operationalState')}>
                            <Grid columns="minmax(0, 1fr) minmax(0, 1fr)" gap="2.4rem">
                                <Radio
                                    name="operationalState"
                                    label={t('asset.assetOperational')}
                                    checked={isOperational === true}
                                    onChange={() => setIsOperational(true)}
                                    disabled={!canEditAllFields}
                                />
                                <Radio
                                    name="operationalState"
                                    label={t('asset.assetOutOfOrder')}
                                    checked={isOperational === false}
                                    onChange={() => setIsOperational(false)}
                                    disabled={!canEditAllFields}
                                />
                            </Grid>
                            {isSubmitted && validationErrors.isOperational &&
                                <ValidationMessage>{t(validationErrors.isOperational.message)}</ValidationMessage>
                            }
                        </FormField>
                        <FormField
                            label={t('asset.condition')}
                            helpTooltip={{ content: t('asset.conditionTooltip') }}>
                            <SliderCondition
                                value={condition}
                                defaultValue={latestCondition}
                                onChange={setCondition}
                                disabled={!canEditAllFields}
                                validationMessage={isSubmitted ? validationErrors.condition?.message : undefined}
                            />
                        </FormField>
                        <FormField
                            label={t('assetInspection.fuel')}
                            optional={canEditAllFields && !isFuelRequired}
                            after={canEditAllFields && typeof fuel === "number" && !isFuelRequired
                                ? <Button
                                    theme="link"
                                    icon={<IconReset />}
                                    label={t('common.reset')}
                                    onClick={() => setFuel(null)}
                                />
                                : null
                            }>
                            <SliderFuel
                                value={fuel}
                                defaultValue={latestFuel}
                                onChange={setFuel}
                                disabled={!canEditAllFields}
                                validationMessage={isSubmitted ? validationErrors.fuel?.message : undefined}
                            />
                        </FormField>
                        <FormField label={t('asset.currentOperatingHours')}
                            isText={!canEditAllFields}
                            optional={canEditAllFields && !isOperationHoursRequired}>
                            {canEditAllFields
                                ? <InputNumber
                                    value={operationHours}
                                    onChange={value => setOperationHours(value)}
                                    after={<span className="px-2">{t('common.hours')}</span>}
                                    isSubmitted={isSubmitted}
                                    isInvalid={!!validationErrors.operationHours}
                                    disabled={!canEditAllFields}
                                />
                                : (typeof inspection.operationHours === 'number'
                                    ? t('common.xHours', { count: inspection.operationHours })
                                    : <NoValue />
                                )
                            }
                            {isSubmitted && validationErrors.operationHours &&
                                <ValidationMessage>{t(validationErrors.operationHours.message)}</ValidationMessage>
                            }
                        </FormField>
                        <FormField label={t('assetInspection.mileage')}
                            isText={!canEditAllFields}
                            optional={canEditAllFields}>
                            {canEditAllFields
                                ? <InputNumber
                                    value={mileage}
                                    onChange={value => setMileage(value)}
                                    after={<span className="px-2">{t('common.km')}</span>}
                                    isSubmitted={isSubmitted}
                                    isInvalid={!!validationErrors.mileage}
                                    disabled={!canEditAllFields}
                                />
                                : (typeof inspection.mileage === 'number'
                                    ? `${formatNumber(inspection.mileage)} ${t('common.km')}`
                                    : <NoValue />
                                )
                            }
                            {isSubmitted && validationErrors.mileage &&
                                <ValidationMessage>{t(validationErrors.mileage.message)}</ValidationMessage>
                            }
                        </FormField>
                        <FormField
                            label={t('assetInspection.adBlue')}
                            optional={canEditAllFields}
                            after={canEditAllFields && typeof adBlue === "number"
                                ? <Button
                                    theme="link"
                                    icon={<IconReset />}
                                    label={t('common.reset')}
                                    onClick={() => setAdBlue(null)}
                                />
                                : null
                            }>
                            <SliderFuel
                                value={adBlue}
                                defaultValue={latestAdBlue}
                                onChange={setAdBlue}
                                disabled={!canEditAllFields}
                            />
                        </FormField>
                        {inspectionType === AssetInspectionType.return && (
                            <FormField
                                label={t('assetInspection.refuel')}
                                optional={canEditAllFields}
                                after={canEditAllFields && typeof refuelNumber === "number"
                                    ? <Button
                                        theme="link"
                                        icon={<IconReset />}
                                        label={t('common.reset')}
                                        onClick={() => setRefuel("")}
                                    />
                                    : null
                                }>
                                {canEditAllFields
                                    ? (
                                        <>
                                            <InputNumber
                                                value={refuel}
                                                onChange={setRefuel}
                                                after={<span className="px-2">{t('assetInspection.refuelLiters')}</span>}
                                                isSubmitted={isSubmitted}
                                                isInvalid={!!validationErrors.refuel}
                                                disabled={!canEditAllFields}
                                            />
                                            {isSubmitted && validationErrors.refuel &&
                                                <ValidationMessage>{t(validationErrors.refuel.message)}</ValidationMessage>
                                            }
                                        </>
                                    )
                                    : (
                                        typeof inspection?.refuel === "number"
                                            ? `${inspection.refuel} ${t('assetInspection.refuelLiters')}`
                                            : "–"
                                    )
                                }
                            </FormField>
                        )}
                        <FormField
                            label={t('assetInspection.lsva')}
                            optional={canEditAllFields}
                            after={canEditAllFields && typeof lsvaNumber === "number"
                                ? <Button
                                    theme="link"
                                    icon={<IconReset />}
                                    label={t('common.reset')}
                                    onClick={() => setLsva("")}
                                />
                                : null
                            }>
                            {canEditAllFields
                                ? (
                                    <>
                                        <InputNumber
                                            value={lsva}
                                            placeholder={t('assetInspection.lsvaPlaceholder')}
                                            onChange={setLsva}
                                            isSubmitted={isSubmitted}
                                            isInvalid={!!validationErrors.lsva}
                                            disabled={!canEditAllFields}
                                        />
                                        {isSubmitted && validationErrors.lsva &&
                                            <ValidationMessage>{t(validationErrors.lsva.message)}</ValidationMessage>
                                        }
                                    </>
                                )
                                : (
                                    typeof inspection?.lsva === "number"
                                        ? formatNumber(inspection.lsva)
                                        : "–"
                                )
                            }
                        </FormField>
                        <GridItem>
                            <Flex alignItems="center" justifyContent="space-between" gap="1.6rem" className="mt-1 mb-1-5">
                                <FlexItem>{t(inspection ? 'common.pictures' : 'asset.addPictures')}</FlexItem>
                                <FlexItem className="color-grey-500">{t('common.xPicturesAdded', { count: inspection?.images?.images?.length ?? imageCollectionFilesToUpload.length })}</FlexItem>
                            </Flex>
                            {inspection
                                ? <AssetInspectionModalImageViewer
                                    collection={inspection.images}
                                />
                                : <AssetInspectionModalImageEditor
                                    files={imageCollectionFilesToUpload}
                                    tags={imageCollectionTags}
                                    fixedTags={imageCollectionFixedTags}
                                    onChange={handleImagesChange}
                                />
                            }
                            {isSubmitted && validationErrors.images &&
                                <ValidationMessage>{t('validation.requiredPictures')}</ValidationMessage>
                            }
                        </GridItem>
                        <FormField label={t('common.comments')} optional>
                            <Input
                                type="textarea"
                                maxLength={INPUT_MAX_LENGTH_LARGE}
                                value={comment ?? ""}
                                onChange={e => setComment(e.target.value)}
                            />
                        </FormField>
                    </Grid>
                </Form>
            </ConfirmModal>
        </>
    )
}

export default AssetInspectionModal
